import React, { useState, useEffect } from 'react';
import { 
  X, User, Mail, Phone, MapPin, Building, Calendar, 
  Users as UsersIcon, FileText, BookOpen, ShoppingBag, MessageCircle,
  ToggleLeft, ToggleRight, Save, Edit2, Briefcase, Hash, Globe
} from 'lucide-react';

const UtilisateurDetail = ({ utilisateur, onClose, onUpdate, onToggleStatus, userStats }) => {
  const [activeTab, setActiveTab] = useState('info');
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    telephone: '',
    bio: '',
    raison_social: '',
    secteur_activite: '',
    type_entreprise: ''
  });

  useEffect(() => {
    if (utilisateur) {
      setFormData({
        first_name: utilisateur.first_name || '',
        last_name: utilisateur.last_name || '',
        email: utilisateur.email || '',
        telephone: utilisateur.telephone || '',
        bio: utilisateur.bio || '',
        raison_social: utilisateur.raison_social || '',
        secteur_activite: utilisateur.secteur_activite || '',
        type_entreprise: utilisateur.type_entreprise || ''
      });
    }
  }, [utilisateur]);

  const handleSubmit = async () => {
    await onUpdate(utilisateur.id, formData);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setFormData({
      first_name: utilisateur.first_name || '',
      last_name: utilisateur.last_name || '',
      email: utilisateur.email || '',
      telephone: utilisateur.telephone || '',
      bio: utilisateur.bio || '',
      raison_social: utilisateur.raison_social || '',
      secteur_activite: utilisateur.secteur_activite || '',
      type_entreprise: utilisateur.type_entreprise || ''
    });
    setIsEditing(false);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = () => {
    if (utilisateur.is_active) {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
          Actif
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
        Inactif
      </span>
    );
  };

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-lg w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* En-tête */}
        <div className="relative">
          {/* Photo de couverture */}
          {utilisateur.photo_couverture && (
            <div className="h-48 overflow-hidden">
              <img
                src={utilisateur.photo_couverture}
                alt="Couverture"
                className="w-full h-full object-cover"
              />
            </div>
          )}
          
          {/* Bouton fermer */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 bg-white rounded-lg hover:bg-gray-100 transition-colors shadow-lg z-10"
          >
            <X className="w-6 h-6" />
          </button>

          {/* Photo de profil et infos de base */}
          <div className="relative px-6 pb-4">
            <div className="flex items-end gap-4">
              {utilisateur.image ? (
                <img
                  src={utilisateur.image}
                  alt={utilisateur.username}
                  className={`w-32 h-32 rounded-full border-4 border-white object-cover ${
                    utilisateur.photo_couverture ? '-mt-16' : 'mt-4'
                  }`}
                />
              ) : (
                <div className={`w-32 h-32 rounded-full border-4 border-white bg-gray-200 flex items-center justify-center ${
                  utilisateur.photo_couverture ? '-mt-16' : 'mt-4'
                }`}>
                  <User className="w-16 h-16 text-gray-400" />
                </div>
              )}

              <div className="flex-1 pb-2">
                <div className="flex items-center gap-3 mb-1">
                  <h2 className="text-2xl font-bold text-gray-900">
                    {utilisateur.full_name || utilisateur.username}
                  </h2>
                  {getStatusBadge()}
                </div>
                <p className="text-gray-600">@{utilisateur.username}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b">
          <div className="flex px-6">
            <button
              onClick={() => setActiveTab('info')}
              className={`px-6 py-3 font-medium transition-colors ${
                activeTab === 'info'
                  ? 'text-[#FF6B00] border-b-2 border-[#FF6B00]'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Informations
            </button>
            <button
              onClick={() => setActiveTab('stats')}
              className={`px-6 py-3 font-medium transition-colors ${
                activeTab === 'stats'
                  ? 'text-[#FF6B00] border-b-2 border-[#FF6B00]'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Statistiques
            </button>
          </div>
        </div>

        {/* Contenu */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'info' && (
            <div className="space-y-6">
              {/* Informations personnelles */}
              <div className="bg-gray-50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Informations personnelles
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                      <User className="w-4 h-4" />
                      Prénom
                    </label>
                    {isEditing ? (
                      <input
                        type="text"
                        value={formData.first_name}
                        onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6B00] focus:border-transparent"
                      />
                    ) : (
                      <p className="text-gray-900">{utilisateur.first_name || 'Non renseigné'}</p>
                    )}
                  </div>

                  <div>
                    <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                      <User className="w-4 h-4" />
                      Nom
                    </label>
                    {isEditing ? (
                      <input
                        type="text"
                        value={formData.last_name}
                        onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6B00] focus:border-transparent"
                      />
                    ) : (
                      <p className="text-gray-900">{utilisateur.last_name || 'Non renseigné'}</p>
                    )}
                  </div>

                  <div>
                    <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                      <Mail className="w-4 h-4" />
                      Email
                    </label>
                    {isEditing ? (
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6B00] focus:border-transparent"
                      />
                    ) : (
                      <p className="text-gray-900">{utilisateur.email || 'Non renseigné'}</p>
                    )}
                  </div>

                  <div>
                    <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                      <Phone className="w-4 h-4" />
                      Téléphone
                    </label>
                    {isEditing ? (
                      <input
                        type="tel"
                        value={formData.telephone}
                        onChange={(e) => setFormData({ ...formData, telephone: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6B00] focus:border-transparent"
                      />
                    ) : (
                      <p className="text-gray-900">{utilisateur.telephone || 'Non renseigné'}</p>
                    )}
                  </div>
                </div>

                {/* Bio */}
                <div className="mt-4">
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                    <FileText className="w-4 h-4" />
                    Bio
                  </label>
                  {isEditing ? (
                    <textarea
                      value={formData.bio}
                      onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                      rows={3}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6B00] focus:border-transparent"
                    />
                  ) : (
                    <p className="text-gray-900 whitespace-pre-wrap">
                      {utilisateur.bio || 'Aucune bio'}
                    </p>
                  )}
                </div>
              </div>

              {/* Informations professionnelles */}
              <div className="bg-gray-50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Informations professionnelles
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                      <Building className="w-4 h-4" />
                      Raison sociale
                    </label>
                    {isEditing ? (
                      <input
                        type="text"
                        value={formData.raison_social}
                        onChange={(e) => setFormData({ ...formData, raison_social: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6B00] focus:border-transparent"
                      />
                    ) : (
                      <p className="text-gray-900">{utilisateur.raison_social || 'Non renseigné'}</p>
                    )}
                  </div>

                  <div>
                    <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                      <Briefcase className="w-4 h-4" />
                      Type d'entreprise
                    </label>
                    {isEditing ? (
                      <input
                        type="text"
                        value={formData.type_entreprise}
                        onChange={(e) => setFormData({ ...formData, type_entreprise: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6B00] focus:border-transparent"
                      />
                    ) : (
                      <p className="text-gray-900">{utilisateur.type_entreprise || 'Non renseigné'}</p>
                    )}
                  </div>

                  <div className="md:col-span-2">
                    <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                      <Globe className="w-4 h-4" />
                      Secteur d'activité
                    </label>
                    {isEditing ? (
                      <input
                        type="text"
                        value={formData.secteur_activite}
                        onChange={(e) => setFormData({ ...formData, secteur_activite: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6B00] focus:border-transparent"
                      />
                    ) : (
                      <p className="text-gray-900">{utilisateur.secteur_activite || 'Non renseigné'}</p>
                    )}
                  </div>

                  {utilisateur.nif && (
                    <div>
                      <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                        <Hash className="w-4 h-4" />
                        NIF
                      </label>
                      <p className="text-gray-900">{utilisateur.nif}</p>
                    </div>
                  )}

                  {utilisateur.rccm && (
                    <div>
                      <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                        <Hash className="w-4 h-4" />
                        RCCM
                      </label>
                      <p className="text-gray-900">{utilisateur.rccm}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Informations système */}
              <div className="bg-gray-50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Informations système
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                      <Calendar className="w-4 h-4" />
                      Date d'inscription
                    </label>
                    <p className="text-gray-900">{formatDate(utilisateur.date_joined)}</p>
                  </div>

                  <div>
                    <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                      <UsersIcon className="w-4 h-4" />
                      Nombre d'amis
                    </label>
                    <p className="text-gray-900">{utilisateur.amis_count || 0}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'stats' && userStats && (
            <div className="space-y-6">
              {/* Publications */}
              <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-6 border-l-4 border-green-500">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-3 bg-green-500 rounded-lg">
                      <FileText className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">Publications</h3>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Total</p>
                    <p className="text-3xl font-bold text-green-600">
                      {userStats.publications?.total || 0}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Signalées</p>
                    <p className="text-3xl font-bold text-red-600">
                      {userStats.publications?.signalees || 0}
                    </p>
                  </div>
                </div>
              </div>

              {/* Formations */}
              <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-lg p-6 border-l-4 border-yellow-500">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-3 bg-yellow-500 rounded-lg">
                      <BookOpen className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">Formations</h3>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Total</p>
                    <p className="text-3xl font-bold text-yellow-600">
                      {userStats.formations?.total || 0}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Inscriptions</p>
                    <p className="text-3xl font-bold text-yellow-600">
                      {userStats.formations?.inscriptions || 0}
                    </p>
                  </div>
                </div>
              </div>

              {/* Événements */}
              <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-6 border-l-4 border-purple-500">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-3 bg-purple-500 rounded-lg">
                      <Calendar className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">Événements</h3>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Total</p>
                    <p className="text-3xl font-bold text-purple-600">
                      {userStats.evenements?.total || 0}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Participants</p>
                    <p className="text-3xl font-bold text-purple-600">
                      {userStats.evenements?.participants || 0}
                    </p>
                  </div>
                </div>
              </div>

              {/* Produits/Services */}
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-6 border-l-4 border-blue-500">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-3 bg-blue-500 rounded-lg">
                      <ShoppingBag className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">Produits & Services</h3>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Total</p>
                    <p className="text-3xl font-bold text-blue-600">
                      {userStats.produits?.total || 0}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Actifs</p>
                    <p className="text-3xl font-bold text-green-600">
                      {userStats.produits?.actifs || 0}
                    </p>
                  </div>
                </div>
              </div>

              {/* Groupes */}
              <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-lg p-6 border-l-4 border-indigo-500">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-3 bg-indigo-500 rounded-lg">
                      <UsersIcon className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">Groupes</h3>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Admin</p>
                    <p className="text-3xl font-bold text-indigo-600">
                      {userStats.groupes?.admin || 0}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Membre</p>
                    <p className="text-3xl font-bold text-indigo-600">
                      {userStats.groupes?.membre || 0}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Total</p>
                    <p className="text-3xl font-bold text-indigo-600">
                      {userStats.groupes?.total || 0}
                    </p>
                  </div>
                </div>
              </div>

              {/* Social & Communication */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white rounded-lg shadow p-6 border-l-4 border-pink-500">
                  <h4 className="font-semibold text-gray-900 mb-4">Social</h4>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Amis</span>
                      <span className="font-bold text-pink-600">
                        {userStats.social?.amis || 0}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Demandes envoyées</span>
                      <span className="font-bold text-gray-900">
                        {userStats.social?.demandes_envoyees || 0}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Demandes reçues</span>
                      <span className="font-bold text-gray-900">
                        {userStats.social?.demandes_recues || 0}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg shadow p-6 border-l-4 border-cyan-500">
                  <h4 className="font-semibold text-gray-900 mb-4">Communication</h4>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Conversations</span>
                      <span className="font-bold text-cyan-600">
                        {userStats.communication?.conversations || 0}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Messages envoyés</span>
                      <span className="font-bold text-gray-900">
                        {userStats.communication?.messages_envoyes || 0}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="border-t p-6 bg-gray-50">
          <div className="flex items-center justify-between">
            <div className="flex gap-3">
              {isEditing ? (
                <>
                  <button
                    onClick={handleSubmit}
                    className="flex items-center gap-2 px-6 py-2 bg-[#437011] text-white rounded-lg hover:bg-[#356009] transition-colors"
                  >
                    <Save className="w-4 h-4" />
                    Enregistrer
                  </button>
                  <button
                    onClick={handleCancel}
                    className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                  >
                    Annuler
                  </button>
                </>
              ) : (
                <button
                  onClick={() => setIsEditing(true)}
                  className="flex items-center gap-2 px-6 py-2 bg-[#FF6B00] text-white rounded-lg hover:bg-[#E55A00] transition-colors"
                >
                  <Edit2 className="w-4 h-4" />
                  Modifier
                </button>
              )}
            </div>

            <button
              onClick={() => onToggleStatus(utilisateur.id)}
              className={`flex items-center gap-2 px-6 py-2 rounded-lg transition-colors ${
                utilisateur.is_active
                  ? 'bg-red-100 text-red-600 hover:bg-red-200'
                  : 'bg-green-100 text-green-600 hover:bg-green-200'
              }`}
            >
              {utilisateur.is_active ? (
                <>
                  <ToggleLeft className="w-4 h-4" />
                  Désactiver
                </>
              ) : (
                <>
                  <ToggleRight className="w-4 h-4" />
                  Activer
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UtilisateurDetail;
